import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Popover, Tooltip } from 'antd';
import { Bell, MailWarning, PackageX, TriangleAlert } from 'lucide-react';

import { useProducts } from '../../hooks';
import { useI18n } from '../../i18n';
import { createLink } from '../../utils';
import { ROUTE_URLS } from '../../routes';
import { EmptyState, getStockStatus } from '../ui';

export const Notifications = () => {
  const { isAdmin, user } = useSelector(state => state.authReducer);
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { data: products } = useProducts({ enabled: isAdmin });
  const isEmailMissing = Boolean(user) && !user.email;

  const alerts = useMemo(() => {
    if (!isAdmin || !Array.isArray(products)) return [];
    return products
      .filter(p => getStockStatus(p.quantity) !== 'ok')
      .sort((a, b) => a.quantity - b.quantity);
  }, [isAdmin, products]);

  const total = alerts.length + (isEmailMissing ? 1 : 0);

  const content = (
    <div className='wm-popover'>
      <div className='wm-popover__head'>
        {t('header.notifications')}
        {total > 0 && <span className='wm-badge wm-badge--warning wm-badge--plain'>{total}</span>}
      </div>
      <div className='wm-popover__body'>
        {isEmailMissing && (
          <Link to={createLink(ROUTE_URLS.EDIT_USER, user.id)} className='wm-notice' onClick={() => setOpen(false)}>
            <span className='wm-notice__icon'>
              <MailWarning size={15} />
            </span>
            <span style={{ minWidth: 0 }}>
              <div className='wm-notice__title'>{t('users.emailMissingTitle')}</div>
              <div className='wm-notice__text'>{t('users.emailMissingText')}</div>
            </span>
          </Link>
        )}
        {total === 0 ? (
          <EmptyState compact icon={Bell} title={t('header.allGood')} text={t('header.noNotifications')} />
        ) : (
          alerts.map(product => {
            const out = getStockStatus(product.quantity) === 'out';
            return (
              <Link
                key={product.id}
                to={createLink(ROUTE_URLS.EDIT_PRODUCT, product.id)}
                className='wm-notice'
                onClick={() => setOpen(false)}
              >
                <span className={`wm-notice__icon${out ? ' wm-notice__icon--danger' : ''}`}>
                  {out ? <PackageX size={15} /> : <TriangleAlert size={15} />}
                </span>
                <span style={{ minWidth: 0 }}>
                  <div className='wm-notice__title'>{product.name}</div>
                  <div className='wm-notice__text'>
                    {out ? t('header.outOfStockItem') : t('header.lowStockItem', { count: product.quantity })}
                  </div>
                </span>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger='click'
      placement='bottomRight'
      open={open}
      onOpenChange={setOpen}
      arrow={false}
    >
      <Tooltip title={t('header.notifications')} placement='bottom'>
        <button type='button' className='wm-icon-btn' aria-label={t('header.notifications')}>
          <Bell size={18} strokeWidth={1.75} />
          {total > 0 && <span className='wm-dot'>{total > 9 ? '9+' : total}</span>}
        </button>
      </Tooltip>
    </Popover>
  );
};
